import React, { useEffect, useRef, useState } from "react";
import { X, Send, Music, Trash2 } from "lucide-react";
import { useChat } from "../context/ChatContext";
import { useAdmin } from "../context/AdminContext";
import { STATIONS } from "../config/stations";
import { toast } from "sonner";

const ChatDrawer = () => {
  const {
    drawerOpen,
    setDrawerOpen,
    messages,
    displayName,
    setName,
    sendMessage,
    sendRequest,
    connected,
  } = useChat();
  const { isAdmin, authFetch } = useAdmin();

  const [tab, setTab] = useState("chat");
  const [text, setText] = useState("");
  const [tempName, setTempName] = useState(displayName || "");
  const [song, setSong] = useState("");
  const [station, setStation] = useState("fm151");
  const [note, setNote] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, drawerOpen, tab]);

  // Surface moderation feedback from server
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.__gfnOnModeration = (reason) => toast.error(reason || "Message blocked");
    return () => {
      window.__gfnOnModeration = null;
    };
  }, []);

  const adminDelete = async (id) => {
    if (!isAdmin) return;
    const res = await authFetch(`/api/admin/chat/messages/${id}`, { method: "DELETE" });
    if (res.ok) toast.success("Deleted");
    else toast.error("Delete failed");
  };

  const submitChat = (e) => {
    e.preventDefault();
    if (!tempName.trim()) {
      toast.error("Enter a name to chat");
      return;
    }
    if (!displayName || displayName !== tempName) setName(tempName.trim());
    if (!text.trim()) return;
    sendMessage(text);
    setText("");
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!tempName.trim()) return toast.error("Enter a name first");
    if (!song.trim()) return toast.error("Enter a song");
    if (!displayName || displayName !== tempName) setName(tempName.trim());
    const res = await sendRequest({ song, station, note });
    if (res.blocked) {
      toast.error(res.reason || "Blocked by moderation");
    } else if (res.ok) {
      toast.success("Request sent to the DJ booth");
      setSong("");
      setNote("");
    } else {
      toast.error("Could not send request");
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={() => setDrawerOpen(false)}
        className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          drawerOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />
      <aside
        data-testid="chat-drawer"
        className={`fixed top-0 right-0 z-50 h-full w-full sm:w-96 bg-[#0a0a0a] border-l border-gold/30 shadow-[0_0_60px_rgba(212,175,55,0.15)] transform transition-transform duration-300 ${
          drawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-gold/20">
          <div>
            <div className="font-display text-2xl text-gold-gradient">LIVE ROOM</div>
            <div className="font-condensed text-[10px] tracking-[0.3em] text-neutral-400 mt-0.5">
              {connected ? "● CONNECTED" : "○ CONNECTING…"}
            </div>
          </div>
          <button
            data-testid="close-chat-btn"
            onClick={() => setDrawerOpen(false)}
            className="text-neutral-400 hover:text-gold"
            aria-label="Close chat"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {/* Tabs */}
        <div className="flex border-b border-neutral-900">
          {[
            { id: "chat", label: "Chat" },
            { id: "request", label: "Song Request" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex-1 py-3 font-condensed uppercase tracking-widest text-xs transition-colors ${
                tab === t.id ? "text-gold border-b-2 border-gold" : "text-neutral-500 hover:text-gold"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "chat" ? (
          <div className="flex flex-col h-[calc(100%-8.75rem)]">
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-4 py-4 space-y-2"
              data-testid="chat-messages"
            >
              {messages.length === 0 && (
                <div className="text-center text-neutral-500 font-body text-sm py-16">
                  Be the first to say hello. Type your name below and drop a line.
                </div>
              )}
              {messages.map((m) => {
                const mine = displayName && m.name === displayName;
                return (
                  <div
                    key={m.id}
                    className={`group relative rounded-md px-3 py-2 ${
                      mine
                        ? "bg-crimson/10 border-l-2 border-crimson ml-6"
                        : "bg-neutral-900/70 mr-6"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`font-condensed uppercase tracking-widest text-[10px] ${mine ? "text-crimson" : "text-gold"}`}>
                        {m.name}
                      </span>
                      <span className="text-neutral-600 text-[10px]">
                        {new Date(m.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                    <div className="text-sm text-neutral-100 break-words">{m.message}</div>
                    {isAdmin && (
                      <button
                        data-testid={`admin-inline-delete-${m.id}`}
                        onClick={() => adminDelete(m.id)}
                        className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 text-neutral-500 hover:text-crimson-live transition-opacity"
                        aria-label="Delete message"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <form onSubmit={submitChat} className="px-4 py-3 border-t border-neutral-900 space-y-2">
              <input
                data-testid="chat-name-input"
                value={tempName}
                onChange={(e) => setTempName(e.target.value)}
                placeholder="Your name"
                maxLength={32}
                className="w-full bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
              />
              <div className="flex gap-2">
                <input
                  data-testid="chat-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Type a message…"
                  maxLength={500}
                  className="flex-1 bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
                />
                <button
                  data-testid="chat-send-btn"
                  type="submit"
                  className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-gold hover:bg-gold-light text-black transition-colors"
                  aria-label="Send"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </form>
          </div>
        ) : (
          <form onSubmit={submitRequest} className="p-4 space-y-3 h-[calc(100%-8.75rem)] overflow-y-auto">
            <input
              data-testid="req-name-input"
              value={tempName}
              onChange={(e) => setTempName(e.target.value)}
              placeholder="Your name"
              maxLength={32}
              className="w-full bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
            />
            <input
              data-testid="req-song-input"
              value={song}
              onChange={(e) => setSong(e.target.value)}
              placeholder="Song / artist"
              maxLength={120}
              className="w-full bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
            />
            <select
              data-testid="req-station-select"
              value={station}
              onChange={(e) => setStation(e.target.value)}
              className="w-full bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
            >
              {STATIONS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} — {s.tagline}
                </option>
              ))}
            </select>
            <textarea
              data-testid="req-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional shoutout / note"
              maxLength={200}
              rows={3}
              className="w-full bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none resize-none"
            />
            <button
              data-testid="req-submit-btn"
              type="submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-black font-condensed uppercase tracking-widest text-xs px-4 py-3 rounded-md transition-colors"
            >
              <Music className="w-4 h-4" />
              Send Request
            </button>
          </form>
        )}
      </aside>
    </>
  );
};

export default ChatDrawer;
